import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CompanyLogo } from "@/components/common";
import { isFixture } from "@/components/work/workModel";
import { TABS, actionHref, caseSum, nextAction, ago } from "@/lib/sk";
import { Section, Quiet } from "@/components/oversikt/bits";

const SMALL = { 1: "én", 2: "to", 3: "tre", 4: "fire", 5: "fem" };
const words = (n) => SMALL[n] || String(n);

/** Each reason as a short phrase Egil can act on – never the raw system sentence. */
export function shortReason(s) {
  const m = /\((\d+) poster uten pris\)/.exec(s);
  if (m) return `${m[1]} ${m[1] === "1" ? "post mangler" : "poster mangler"} pris`;
  if (s.startsWith("summen er ikke komplett")) return "kalkylen er ikke komplett";
  if (s.startsWith("risikoterskel")) return null;
  if (s.startsWith("risikosjekkliste")) return s.replace("risikosjekkliste", "risikosjekklisten");
  if (s === "fristen er passert") return "tilbudsfristen er passert";
  if (s.startsWith("frist ")) return `tilbudsfrist ${s.slice(6)}`;
  const q = /^(\d+) åpne avklaringer$/.exec(s);
  if (q) return `${q[1]} åpne avklaringer`;
  return s;
}

/** Priority: passed deadline, blocked work, open questions, near deadline, then closeness to a finished quote. */
export const score = (p) => (p.days_left != null && p.days_left < 0 ? -100 : 0) + (p.days_left != null && p.days_left >= 0 ? -40 + Math.min(p.days_left, 30) : 0) - (p.open_clarifications ? 20 : 0) - (p.calc?.sum_kind === "complete" ? 15 : 0) - (p.reasons?.length || 0) * 2;
export const prioritized = (items) => [...(items || [])].sort((a, b) => score(a) - score(b));

/** The situation in one sentence, the rest as quiet facts – red only when something actually is urgent. */
export function situation(p) {
  if (p.status === "mulighet") return { lead: p.days_left < 0 ? "Oppfølgingen av muligheten er passert." : p.days_left === 0 ? "Følg opp muligheten i dag." : "Følg opp muligheten i morgen.", urgent: p.days_left < 0, facts: [] };
  const passed = p.days_left != null && p.days_left < 0;
  const soon = p.days_left != null && p.days_left >= 0 && p.days_left <= 7;
  const facts = (p.reasons || []).map(shortReason).filter((r) => r && !/tilbudsfrist/.test(r));
  if (passed) return { lead: "Tilbudsfristen er passert.", urgent: true, facts };
  if (soon) return { lead: p.days_left === 0 ? "Tilbudsfristen er i dag." : `Tilbudsfristen er om ${p.days_left} ${p.days_left === 1 ? "dag" : "dager"}.`, urgent: p.days_left <= 2, facts };
  const q = p.quote;
  const k = p.kontroll;
  if (q && q.sent_at) return { lead: `Tilbudet er sendt${q.sent_to?.name || q.sent_to?.company ? ` til ${q.sent_to.name || q.sent_to.company}` : ""}.`, urgent: false, facts: facts.filter((f) => !/komplett/.test(f)) };
  if (q && !q.stale && k?.status === "til_kontroll" && q.status !== "kontrollert") return { lead: k.for_me ? "Venter på din kontroll." : `Tilbudet er til kontroll hos ${(k.to || []).map((x) => (x.name || "").split(" ")[0]).join(", ") || "kontrollør"}.`, urgent: false, facts: facts.filter((f) => !/komplett/.test(f)) };
  if (q && !q.stale && p.calc?.sum_kind === "complete") return { lead: q.status === "kontrollert" ? "Tilbudet er godkjent og klart til å sendes." : k?.status === "avvist" ? "Tilbudet ble returnert fra kontroll." : "Tilbudet er ferdig skrevet og klart for kontroll.", urgent: false, facts: facts.filter((f) => !/komplett/.test(f)) };
  if (facts.length) return { lead: `${facts[0][0].toUpperCase()}${facts[0].slice(1)}.`, urgent: false, facts: facts.slice(1) };
  return { lead: null, urgent: false, facts };
}

/** On Hjem the action is the big job, as a verb: continue the calculation while it is incomplete, otherwise the next step. */
export function homeAction(p) {
  const next = nextAction(p);
  if (!next) return null;
  if (["price", "items", "attention", "clarify"].includes(next.key) && p.calc?.sum_kind !== "complete") return { label: p.calc?.sum_kind === "none" ? "Start kalkylen" : "Fortsett kalkylen", tab: "kalkyle" };
  if (next.key === "clarify") return { label: `Svar på ${next.label}`, tab: next.tab };
  if (next.key === "sent") return { label: "Åpne tilbudet", tab: "tilbud" };
  if (next.key === "waiting") return { label: "Åpne kontrollen", tab: "tilbud", href: actionHref(p, next) };
  return { label: next.label, tab: next.tab, href: actionHref(p, next) };
}

/** One sentence a good colleague would say: which job first, and why – composed from the facts, no model needed. */
export function briefing(items) {
  const top = prioritized(items)[0];
  if (!top) return null;
  const s = situation(top);
  const name = top.vessel?.name || top.name;
  const bits = [s.lead ? s.lead.replace(/\.$/, "") : null, ...s.facts.map((f) => f.replace(/^(\d+) åpne avklaringer$/, (_, n) => `${words(Number(n))} ${n === "1" ? "avklaring står åpen" : "avklaringer står åpne"}`))].filter(Boolean);
  if (!bits.length) return `${name} bør tas først.`;
  const tail = bits.length > 1 ? `${bits.slice(0, -1).join(", ")} og ${bits[bits.length - 1]}` : bits[0];
  return `${name} bør tas først. ${tail[0].toUpperCase()}${tail.slice(1)}.`;
}

/** The week in one quiet line, composed from the facts: what to take first, and the totals a colleague would mention. */
export function digest(items) {
  const list = items || [];
  const unpriced = list.filter((p) => p.calc?.sum_kind && p.calc.sum_kind !== "complete").length;
  const toControl = list.filter((p) => p.quote && !p.quote.stale && p.calc?.sum_kind === "complete" && p.quote.status !== "kontrollert" && p.kontroll?.status !== "til_kontroll").length;
  const toSend = list.filter((p) => p.quote && !p.quote.stale && p.quote.status === "kontrollert" && !p.quote.sent_at).length;
  const open = list.reduce((n, p) => n + (p.open_clarifications || 0), 0);
  const passed = list.filter((p) => p.status !== "mulighet" && p.days_left != null && p.days_left < 0).length;
  return [
    passed ? `${passed} ${passed === 1 ? "frist er passert" : "frister er passert"}` : null,
    unpriced ? `${unpriced} ${unpriced === 1 ? "sak mangler priser" : "saker mangler priser"}` : null,
    toControl ? `${toControl} ${toControl === 1 ? "tilbud er klart til kontroll" : "tilbud er klare til kontroll"}` : null,
    toSend ? `${toSend} ${toSend === 1 ? "tilbud er godkjent og klart til å sendes" : "tilbud er godkjent og klare til å sendes"}` : null,
    open ? `${open} ${open === 1 ? "avklaring venter svar" : "avklaringer venter svar"}` : null,
  ].filter(Boolean);
}

/** One case as a recommendation: name → who/what → the situation in one line → the one next step. A row, not a card.
 *  Only the first (most pressing) row carries a colour; the rest read as plain text – the order already says it. */
function AttentionRow({ p, first }) {
  const act = homeAction(p);
  const s = situation(p);
  const tab = act?.tab && TABS.some((t) => t.key === act.tab) ? `?tab=${act.tab}` : "";
  const href = act?.href || `/p/${p.id}${tab}`;
  const soon = p.days_left != null && p.days_left >= 0 && p.days_left <= 7;
  const tone = first && s.urgent ? "text-[hsl(var(--v-blocked))]" : first && soon ? "text-[hsl(var(--v-assumed))]" : "text-foreground";
  return (
    <article className="group relative -mx-4 flex items-start gap-4 rounded-[16px] px-4 py-4 transition-colors hover:bg-black/[0.025]" data-testid={`oversikt-attention-${p.number}`}>
      <CompanyLogo name={p.customer} size="md" className="mt-0.5 h-9 w-9 shrink-0 rounded-[10px] p-1" />
      <div className="min-w-0 flex-1">
        <Link to={`/p/${p.id}`} className="block truncate text-[16px] font-semibold tracking-[-0.012em] text-foreground after:absolute after:inset-0 after:rounded-[16px]" data-testid={`oversikt-attention-${p.number}-title`}>{p.name}</Link>
        <div className="mt-0.5 truncate text-[13.5px] text-muted-foreground">{[p.customer, p.vessel?.name && p.vessel.name !== p.name ? p.vessel.name : null, `#${p.number}`].filter(Boolean).join(" · ")}</div>
        <div className="mt-2 text-[14px] leading-[1.55]">
          {s.lead ? <span className={cn("font-medium", tone)} data-testid={`oversikt-lead-${p.number}`}>{s.lead}</span> : null}
          {s.facts.length ? <span className="text-foreground/65" data-testid={`oversikt-reasons-${p.number}`}>{s.lead ? " · " : ""}{s.facts.join(" · ")}</span> : null}
        </div>
        {act ? <Link to={href} className="relative z-10 mt-2 inline-flex items-center gap-1 text-[14px] font-medium text-[hsl(var(--navy))] sm:hidden" data-testid={`oversikt-next-${p.number}-m`}>{act.label} <ArrowRight className="h-3.5 w-3.5" /></Link> : null}
      </div>
      {act ? <Link to={href} className="relative z-10 mt-1 hidden h-9 shrink-0 items-center gap-1.5 rounded-full bg-[hsl(var(--navy)/0.06)] px-3.5 text-[13.5px] font-medium text-[hsl(var(--navy))] transition-colors hover:bg-[hsl(var(--navy)/0.12)] sm:inline-flex" data-testid={`oversikt-next-${p.number}`}>{act.label} <ArrowRight className="h-4 w-4" /></Link> : null}
    </article>
  );
}

export function Attention({ items, open, onMore, intro, title = "Trenger oppmerksomhet" }) {
  const sorted = prioritized(items);
  const shown = open ? sorted : sorted.slice(0, 4);
  return (
    <Section title={title} count={sorted.length || null} intro={intro} testId="oversikt-attention">
      {items.length ? <div className="space-y-1">{shown.map((p, i) => <AttentionRow key={p.id} p={p} first={i === 0} />)}</div> : <Quiet testId="oversikt-attention-empty">Ingenting venter på deg akkurat nå.</Quiet>}
      {sorted.length > 4 && !open ? <button type="button" onClick={onMore} className="mt-2 inline-flex items-center gap-1 text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="oversikt-attention-more">Vis {sorted.length - 4} til</button> : null}
    </Section>
  );
}

/** «MF Husavik · Fjord1 AS · #26097 · for 5 min siden» – the whole row opens the case where you were. */
function RecentRow({ p }) {
  const sum = caseSum(p); const next = nextAction(p); const c = p.calc || {};
  const tabLabel = TABS.find((t) => t.key === (p.last_tab || next?.tab))?.label || "Oversikt";
  const missing = c.missing_count ?? (c.coverage ? Math.max(0, (c.coverage.total || 0) - (c.coverage.priced || 0)) : 0);
  const line = sum.kind === "preliminary" && missing ? `${missing} ${missing === 1 ? "post gjenstår" : "poster gjenstår"}` : sum.kind === "complete" ? (p.quote && !p.quote.stale ? (p.quote.sent_at ? "Tilbudet er sendt" : p.quote.status === "kontrollert" ? "Godkjent – klart til å sendes" : p.kontroll?.status === "til_kontroll" ? "Til kontroll" : "Klart til kontroll") : "Kalkylen er komplett") : null;
  return (
    <Link to={`/p/${p.id}`} className="group -mx-3 flex items-center gap-3.5 rounded-[12px] px-3 py-2.5 transition-colors hover:bg-black/[0.03]" data-testid={`oversikt-recent-${p.number}`}>
      <CompanyLogo name={p.customer?.name} domain={p.customer?.website} size="md" className="h-8 w-8 shrink-0 rounded-[9px] p-1" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14.5px] font-medium">{p.name || p.vessel?.name}</div>
        <div className="truncate text-[13px] text-muted-foreground">{[p.customer?.name, `#${p.number}`, tabLabel, line].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="shrink-0 text-[12.5px] tabular-nums text-muted-foreground/80">{ago(p.updated_at)}</div>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-[transform,opacity] group-hover:translate-x-0 group-hover:opacity-100" />
    </Link>
  );
}

export function Recent({ rows, title = "Nylig jobbet med", limit = 5 }) {
  const list = (rows || []).filter((p) => !isFixture(p)).slice(0, limit);
  return (
    <Section title={title} more="/arbeid" moreLabel="Alt arbeid" testId="oversikt-recent">
      {list.length ? <div className="space-y-0.5">{list.map((p) => <RecentRow key={p.id} p={p} />)}</div> : <Quiet>Ingen arbeider ennå – start med en ny forespørsel.</Quiet>}
    </Section>
  );
}

/** Only when something happened: what people did on the work, newest first – never figures. */
export function SinceLast({ events, threads }) {
  const rows = [...(threads || []).map((t) => ({ key: `t-${t.thread_id}`, to: `/p/${t.case_id}?tab=meldinger&thread=${t.thread_id}`, text: `${t.mentions_me ? "Du ble nevnt i" : "Nytt i"} «${t.subject || "samtale"}»`, sub: [t.name, t.customer].filter(Boolean).join(" · "), strong: true })),
    ...(events || []).map((e, i) => ({ key: `e-${i}`, to: e.thread_id ? `/p/${e.case_id}?tab=meldinger&thread=${e.thread_id}` : `/p/${e.case_id}`, text: e.text, sub: [e.case, e.at ? ago(e.at) : null].filter(Boolean).join(" · "), strong: false }))].slice(0, 7);
  if (!rows.length) return null;
  return (
    <Section title="Siden sist" testId="oversikt-threads">
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.key}>
            <Link to={r.to} className="group -mx-3 block rounded-[12px] px-3 py-2.5 transition-colors hover:bg-black/[0.03]" data-testid={`oversikt-since-${r.key}`}>
              <div className={cn("text-[15px] leading-[1.45]", r.strong ? "font-medium text-foreground" : "text-foreground/85")}>{r.text}</div>
              {r.sub ? <div className="mt-0.5 text-[13.5px] text-muted-foreground">{r.sub}</div> : null}
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}
